<template>
  <ContentWrap>
    <el-alert
      class="mb-15px"
      type="info"
      :closable="false"
      title="平台一览：查看各租户 OSS 已用/配额与预警档。站内信发给租户联系人；短信本轮不做。展示已用以后台缓存为准（日刷 Job 回写）。"
    />
    <el-form class="-mb-15px" :model="queryParams" :inline="true" label-width="80px">
      <el-form-item label="租户名" prop="name">
        <el-input
          v-model="queryParams.name"
          placeholder="模糊搜索"
          clearable
          class="!w-180px"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="范围" prop="externalOnly">
        <el-select v-model="queryParams.externalOnly" class="!w-140px">
          <el-option label="仅外部租户" :value="true" />
          <el-option label="全部租户" :value="false" />
        </el-select>
      </el-form-item>
      <el-form-item label="占比" prop="usageBand">
        <el-select v-model="queryParams.usageBand" class="!w-140px">
          <el-option label="全部" value="ALL" />
          <el-option label="≥80%" value="GE_80" />
          <el-option label="≥95%" value="GE_95" />
          <el-option label="已满/零配额" value="FULL" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" />搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" />重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="租户" min-width="180">
        <template #default="{ row }">
          <div>{{ row.tenantName || '-' }}</div>
          <div class="text-secondary">ID {{ row.tenantId }}</div>
        </template>
      </el-table-column>
      <el-table-column label="类型" width="90" align="center">
        <template #default="{ row }">
          <el-tag v-if="row.tenantType === 1" size="small">内部</el-tag>
          <el-tag v-else type="warning" size="small">外部</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="已用" width="110">
        <template #default="{ row }">{{ formatBytes(row.ossUsed) }}</template>
      </el-table-column>
      <el-table-column label="配额" width="110">
        <template #default="{ row }">
          {{ row.ossQuota == null ? '不限' : formatBytes(row.ossQuota) }}
        </template>
      </el-table-column>
      <el-table-column label="占比" width="90" align="center">
        <template #default="{ row }">
          {{ row.usagePercent == null ? '-' : row.usagePercent + '%' }}
        </template>
      </el-table-column>
      <el-table-column label="当前档" width="90" align="center">
        <template #default="{ row }">
          <el-tag v-if="row.currentLevel" :type="levelTag(row.currentLevel)" size="small">
            {{ row.currentLevel }}%
          </el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="已通知档" width="100" align="center">
        <template #default="{ row }">
          {{ row.warnLevel ? row.warnLevel + '%' : '未通知' }}
        </template>
      </el-table-column>
      <el-table-column label="联系人" min-width="140">
        <template #default="{ row }">
          <div>{{ row.contactName || '-' }}</div>
          <div v-if="row.contactUserId" class="text-secondary">UID {{ row.contactUserId }}</div>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="120" align="center" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="goTenant">租户管理</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      :total="total"
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      @pagination="getList"
    />
  </ContentWrap>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router'
import * as OssApi from '@/api/system/ossCapacity'
import { formatBytes } from '@/api/system/ossCapacity'

defineOptions({ name: 'SystemOssCapacityOverview' })

const router = useRouter()
const loading = ref(false)
const list = ref<OssApi.OssTenantUsageVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  name: undefined as string | undefined,
  externalOnly: true,
  usageBand: 'ALL'
})

const levelTag = (level?: number) => {
  if (level != null && level >= 100) return 'danger'
  if (level != null && level >= 95) return 'warning'
  if (level != null && level >= 80) return 'info'
  return 'info'
}

const getList = async () => {
  loading.value = true
  try {
    const data = await OssApi.getOssTenantUsagePage({
      pageNo: queryParams.pageNo,
      pageSize: queryParams.pageSize,
      name: queryParams.name,
      externalOnly: queryParams.externalOnly,
      usageBand: queryParams.usageBand
    })
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.name = undefined
  queryParams.externalOnly = true
  queryParams.usageBand = 'ALL'
  handleQuery()
}

const goTenant = () => {
  router.push({ path: '/system/tenant' })
}

onMounted(getList)
</script>
